import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from './Icons';
import Footer from './Footer';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white font-sans selection:bg-zinc-900 selection:text-white flex flex-col">
      <nav className="w-full px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white/80 backdrop-blur-sm z-20">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white border-2 border-black rounded-sm flex items-center justify-center">
              <Check className="w-4 h-4 text-black" strokeWidth={3} />
            </div>
            <span className="font-semibold text-lg tracking-tight text-black">GradeMind.ai</span>
          </Link>
        </div>
      </nav>

      <main className="flex-1 w-full max-w-3xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight mb-4">Privacy Policy</h1>
          <p className="text-zinc-500">Last updated: March 15, 2024</p>
        </div>

        <div className="prose prose-zinc max-w-none space-y-12">
          <section>
            <h2 className="text-xl font-bold text-zinc-900 mb-4">1. Introduction</h2>
            <p className="text-zinc-600 leading-relaxed">
              GradeMind ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our automated grading services.
              <br /><br />
              We take student data privacy seriously and comply with applicable educational privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-zinc-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4 text-zinc-600">
              <p><strong>Personal Data:</strong> We collect personally identifiable information such as your name, email address, and institutional affiliation when you register for an account.</p>
              <p><strong>Student Data:</strong> We process student submission data (text, documents) strictly for the purpose of providing grading services. We do not own this data.</p>
              <p><strong>Usage Data:</strong> We automatically collect information about how you interact with our services, such as access times, pages viewed, and IP addresses.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-zinc-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-zinc-600 leading-relaxed">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-zinc-600">
              <li>Provide, operate, and maintain our services.</li>
              <li>Improve, personalize, and expand our services.</li>
              <li>Process transactions and send related information.</li>
              <li>Send you emails, newsletters, and support messages.</li>
              <li>Detect and prevent fraud.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-zinc-900 mb-4">4. Data Storage & Security Infrastructure</h2>
            <p className="text-zinc-600 leading-relaxed">
              We utilize industry-standard infrastructure to secure your data:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-zinc-600 mb-6">
                <li><strong>Database:</strong> Metadata, rubrics, and grading results are stored in secure cloud databases.</li>
                <li><strong>File Storage:</strong> Student assignment files (PDFs, documents) are stored in enterprise-grade object storage containers.</li>
                <li><strong>AI Processing:</strong> Submissions are processed via secure third-party Large Language Model (LLM) APIs. These providers do not use data sent to their APIs to train their foundation models.</li>
            </ul>
            <p className="text-zinc-600 leading-relaxed">
              We employ commercially reasonable technical and organizational measures to protect your data. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-zinc-900 mb-4">5. Student Data & Compliance (FERPA/GDPR)</h2>
            <p className="text-zinc-600 leading-relaxed">
                <strong>Your Role:</strong> You (the Educator or Institution) act as the <strong>Data Controller</strong>. You maintain ownership of all student data uploaded.
                <br /><br />
                <strong>Our Role:</strong> GradeMind acts as a <strong>Data Processor</strong>. We process data solely on your behalf to provide grading services.
                <br /><br />
                <strong>Compliance:</strong> You are responsible for ensuring your use of GradeMind complies with applicable laws (e.g., FERPA in the US, GDPR in the EU). This includes obtaining any necessary consents from students or parents before uploading their work. We recommend removing personally identifiable information (PII) like student names from files before upload whenever possible.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-zinc-900 mb-4">6. Contact Us</h2>
            <p className="text-zinc-600 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:
              <br />
              <a href="mailto:privacy@grademind.ai" className="text-black font-medium underline">privacy@GradeMind.ai</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Privacy;
